import React from 'react';
import { getAttendanceImage, getRankImage } from '../../../models/gamification';


const GamificationBadges = ({ gamifiedActivities, member, achievementsSettings = [], rankSettings = [], awardedAchievementIds = [], awardedAchievements = [] }) => {
    const memberXp = member?.xpTotal || member?.XP || 0;

    const earnedActivities = gamifiedActivities?.filter(act => member?.passedActivityIds?.includes(act.id)) || [];
    const earnedRanks = rankSettings?.filter(rank => memberXp >= rank.minXp) || [];
    
    // Conquistas baseadas nas configurações globais (ex: frequência)
    const settingsAchievements = achievementsSettings?.filter(ach => awardedAchievementIds.includes(ach.id)) || [];
    
    // Conquistas extras vindas de eventos (que têm type === 'extra') e não estão nas globais
    const extraAchievements = awardedAchievements?.filter(ach => ach.type === 'extra') || [];
    
    // Evitar duplicatas internas em cada lista
    const finalSettingsAchievements = Array.from(new Map(settingsAchievements.map(ach => [ach.idAchievement || ach.id, ach])).values());
    const finalExtraAchievements = Array.from(new Map(extraAchievements.map(ach => [ach.idAchievement || ach.id, ach])).values());

    // Ordenar todas as conquistas globais por threshold crescente
    const sortedAllAchievements = [...(achievementsSettings || [])].sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
    
    // Encontrar a próxima conquista global bloqueada
    const nextAchievement = sortedAllAchievements.find(ach => !awardedAchievementIds.includes(ach.id));

    // Ordenar todos os ranks por minXp crescente
    const sortedAllRanks = [...(rankSettings || [])].sort((a, b) => (a.minXp || 0) - (b.minXp || 0));

    // Encontrar o próximo rank bloqueado
    const nextRank = sortedAllRanks.find(rank => memberXp < rank.minXp);

    const getAchievementDescription = (ach, isLocked = false) => {
        if (!ach) return "";
        
        if (ach.type === 'attendance') {
            const threshold = ach.threshold || 0;
            const label = threshold === 1 ? 'aula' : 'aulas';
            return isLocked ? `Frequentar ${threshold} ${label}` : `Frequentou ${threshold} ${label}`;
        }
        
        if (ach.type === 'points') {
            const threshold = ach.threshold || 0;
            return isLocked ? `Alcançar ${threshold} XP` : `Atingiu ${threshold} XP`;
        }
        
        if (ach.type === 'streak') {
            const threshold = ach.threshold || 0;
            const label = threshold === 1 ? 'dia' : 'dias';
            return isLocked ? `Fazer ${threshold} ${label} seguidos` : `Fez ${threshold} ${label} seguidos`;
        }
        
        return ach.description || (isLocked ? `Meta: ${ach.threshold}` : `Conquista desbloqueada!`);
    };

    // Helper para renderizar um item de conquista (conquistado ou bloqueado)
    const renderBadgeItem = (ach, isLocked = false) => {
        const descText = getAchievementDescription(ach, isLocked);
        
        return (
            <div key={ach.id} className="d-flex flex-column align-items-center position-relative" style={itemWrapperStyle}>
                <div 
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ 
                        width: "100%", 
                        aspectRatio: "1 / 1", 
                        backgroundColor: 'transparent', 
                        overflow: 'hidden', 
                        transition: 'all 0.3s ease',
                        filter: isLocked ? 'grayscale(100%) opacity(40%)' : 'none'
                    }}
                >
                    {ach.imageId ? (
                        <img 
                            src={getAttendanceImage(ach.imageId) || ach.imageId} 
                            alt={ach.name} 
                            style={{ 
                                width: "100%", 
                                height: "100%", 
                                objectFit: "cover"
                            }} 
                        />
                    ) : (
                        <i className={`mdi ${ach.icon || 'mdi-medal'}`} style={{ color: isLocked ? '#74788d' : '#f1b44c', fontSize: "clamp(1rem, 2vw, 2rem)" }}></i>
                    )}
                </div>
                
                <span 
                    className={`mt-2 text-center fw-bold w-100 ${isLocked ? 'text-secondary text-muted' : 'text-dark'}`} 
                    style={{ 
                        fontSize: "clamp(0.8rem, 2vw, 1.2rem)", 
                        lineHeight: "1.2", 
                        wordBreak: 'break-word',
                        opacity: isLocked ? 0.7 : 1
                    }}
                >
                    {ach.name}
                    {isLocked && <span className="d-block font-size-10 text-uppercase fw-normal text-muted mt-1">(Próxima)</span>}
                </span>

                {descText && (
                    <span 
                        className="text-center text-muted w-100 mt-1" 
                        style={{ 
                            fontSize: "clamp(0.65rem, 1.4vw, 0.8rem)", 
                            lineHeight: "1.3", 
                            wordBreak: 'break-word',
                            fontWeight: 'normal',
                            opacity: isLocked ? 0.6 : 0.85
                        }}
                    >
                        {descText}
                    </span>
                )}
            </div>
        );
    };

    // Helper para renderizar um item de nível (XP)
    const renderRankItem = (rank, isLocked = false) => {
        const xpText = isLocked ? `Alcançar ${rank.minXp} XP` : `Atingiu ${rank.minXp} XP`;
        
        return (
            <div key={rank.id} className="d-flex flex-column align-items-center position-relative" style={itemWrapperStyle}>
                <div 
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ 
                        width: "100%",
                        aspectRatio: "1 / 1",
                        backgroundColor: 'transparent',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        filter: isLocked ? 'grayscale(100%) opacity(40%)' : 'none'
                    }}
                >
                    {getRankImage(rank.imageId || rank.id) ? (
                        <img 
                            src={getRankImage(rank.imageId || rank.id)} 
                            alt={rank.name} 
                            style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                        />
                    ) : (
                        <i className={`mdi mdi-shield-star`} style={{ color: isLocked ? '#74788d' : '#f1b44c', fontSize: "clamp(1rem, 2vw, 2rem)" }}></i>
                    )}
                </div>
                
                <span 
                    className={`mt-2 text-center fw-bold w-100 ${isLocked ? 'text-secondary text-muted' : 'text-dark'}`} 
                    style={{ 
                        fontSize: "clamp(0.8rem, 2vw, 1.2rem)", 
                        lineHeight: "1.2", 
                        wordBreak: 'break-word',
                        opacity: isLocked ? 0.7 : 1
                    }}
                >
                    {rank.name}
                    {isLocked && <span className="d-block font-size-10 text-uppercase fw-normal text-muted mt-1">(Próxima)</span>}
                </span>

                <span 
                    className="text-center text-muted w-100 mt-1" 
                    style={{ 
                        fontSize: "clamp(0.65rem, 1.4vw, 0.8rem)", 
                        lineHeight: "1.3", 
                        wordBreak: 'break-word',
                        fontWeight: 'normal',
                        opacity: isLocked ? 0.6 : 0.85
                    }}
                >
                    {xpText}
                </span>
            </div>
        );
    };

    const itemWrapperStyle = { 
        width: "calc((100% - 1.5rem) / 4)", 
        maxWidth: "120px", 
        minWidth: "70px" 
    };

    return (
        <div className="w-100 mt-2 pb-4">
            
            {earnedActivities.length > 0 && (
                <>
                    <h5 className="text-dark fw-bold mb-0" style={{ borderLeft: "5px solid #f1b44c", paddingLeft: "12px", fontSize: "1.4rem" }}>Minhas Insígnias</h5>
                    <div 
                        className="d-flex justify-content-start align-items-start py-3 w-100 flex-wrap gap-2"
                    >
                        {earnedActivities.map((act) => {
                            const isCurrent = member?.activityIds?.includes(act.id);
                            
                            return (
                                <div key={act.id} className="d-flex flex-column align-items-center position-relative" style={itemWrapperStyle}>
                                    <div 
                                        className={`rounded-circle d-flex align-items-center justify-content-center ${isCurrent ? 'border border-primary shadow-lg border-2' : ''}`}
                                        style={{ 
                                            width: "100%",
                                            aspectRatio: "1 / 1",
                                            backgroundColor: "transparent",
                                            overflow: 'hidden',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {act.photoUrl ? (
                                            <img 
                                                src={act.photoUrl} 
                                                alt={act.name} 
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                                            />
                                        ) : (
                                            <i className="mdi mdi-shield-star" style={{ color: '#f1b44c', fontSize: "clamp(1rem, 2vw, 2rem)" }}></i>
                                        )}
                                    </div>
                                    <span className="mt-2 text-center text-dark fw-bold w-100" style={{ fontSize: "clamp(0.8rem, 2vw, 1.2rem)", lineHeight: "1.2", wordBreak: 'break-word' }}>
                                        {act.name}
                                    </span>
                                    <span 
                                        className="text-center text-muted w-100 mt-1" 
                                        style={{ 
                                            fontSize: "clamp(0.65rem, 1.5vw, 0.8rem)", 
                                            lineHeight: "1.3", 
                                            wordBreak: 'break-word',
                                            fontWeight: 'normal',
                                            opacity: 0.8
                                        }}
                                    >
                                        {isCurrent ? "Nível Atual" : "Aprovado"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {(earnedRanks.length > 0 || nextRank) && (
                <>
                    <h5 className="text-dark fw-bold mb-0 mt-4" style={{ borderLeft: "5px solid #f1b44c", paddingLeft: "12px", fontSize: "1.4rem" }}>Conquistas por XP</h5>
                    <div 
                        className="d-flex justify-content-start align-items-start py-3 w-100 flex-wrap gap-2"
                    >
                        {earnedRanks.map((rank) => renderRankItem(rank, false))}
                        {nextRank && renderRankItem(nextRank, true)}
                    </div>
                </>
            )}

            {(finalSettingsAchievements.length > 0 || nextAchievement) && (
                <>
                    <h5 className="text-dark fw-bold mb-0 mt-4" style={{ borderLeft: "5px solid #f1b44c", paddingLeft: "12px", fontSize: "1.4rem" }}>Conquistas por Presença</h5>
                    <div className="d-flex justify-content-start align-items-start py-3 w-100 flex-wrap gap-2">
                        {finalSettingsAchievements.map((ach) => renderBadgeItem(ach, false))}
                        {nextAchievement && renderBadgeItem(nextAchievement, true)}
                    </div>
                </>
            )}

            {finalExtraAchievements.length > 0 && (
                <>
                    <h5 className="text-dark fw-bold mb-0 mt-4" style={{ borderLeft: "5px solid #f1b44c", paddingLeft: "12px", fontSize: "1.4rem" }}>Eventos Extras</h5>
                    <div className="d-flex justify-content-start align-items-start py-3 w-100 flex-wrap gap-2">
                        {finalExtraAchievements.map((ach) => renderBadgeItem(ach, false))}
                    </div>
                </>
            )}

        </div>
    );
};

export default GamificationBadges;
